import { useEffect, useRef, useCallback } from 'react';

interface SoundOptions {
  playbackRate?: number;
}

export function useSound(soundUrl: string, options: SoundOptions = {}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { playbackRate = 1 } = options;

  // Initialize audio on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      audioRef.current = new Audio(soundUrl);

      // Set the playback rate
      if (audioRef.current && playbackRate !== 1) {
        audioRef.current.playbackRate = playbackRate;
      }

      // Clean up on unmount
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      };
    }
  }, [soundUrl, playbackRate]);

  // Play sound function that checks localStorage directly
  const play = useCallback(() => {
    // Check the global sound setting from localStorage
    const soundEnabled = localStorage.getItem('sound-enabled') !== 'false';

    if (soundEnabled && audioRef.current) {
      // Reset the audio to the beginning if it's already playing
      audioRef.current.currentTime = 0;

      // Ensure playback rate is set (in case it was changed)
      audioRef.current.playbackRate = playbackRate;

      // Play the sound
      audioRef.current.play().catch(error => {
        // Handle autoplay restrictions gracefully
        console.warn('Could not play sound:', error);
      });
    }
  }, [playbackRate]);

  return { play };
}
